<template>
  <div class="body-wrapper">
    <div class="video-wrapper">
      <!-- Video Player -->
      <div v-if="videoUrl" class="video-container">
        <video
            ref="videoPlayer"
            class="video-element"
            controls
            preload="metadata"
            :poster="coverImageUrl"
        >
          <source :src="videoUrl" type="application/x-mpegURL">
          Your browser does not support the video tag.
        </video>
      </div>

      <!-- No Video State -->
      <div v-else class="no-video-state">
        <img src="@/assets/images/noDataBox.svg" width="80" height="80" alt="No Video">
        <span class="no-video-text">No video available</span>
      </div>
    </div>

    <div class="tabs-view">
      <div class="active-tab">
        <span class="active-tab-text">Notes</span>
      </div>

      <img src="@/assets/images/exportMail.svg" width="18" height="18" alt="Export Icon" />
    </div>

    <div class="divider" />

    <div class="notes-wrapper">
      <div class="note-holder">
        <img class="no-data-icon" src="@/assets/images/noDataBox.svg">
        <span class="note-title">{{ $t("no_note") }}</span>
        <span class="note-info">{{ $t("no_notes_description") }}</span>
      </div>
    </div>

    <div class="video-footer">
      <input class="text-input" type="text" placeholder="Add a note..." />
      <img src="@/assets/images/sendCircle.svg" width="24" height="24" alt="Send Icon" />
    </div>
  </div>
</template>

<script>
import Hls from 'hls.js'

export default {
  props: {
    questionData: {
      type: Object,
      required: true
    }
  },

  data() {
    return {
      hls: null
    }
  },

  computed: {
    videoUrl() {
      if (!this.questionData?.content?.attach) return null

      const videoAttachment = this.questionData.content.attach.find(
          attachment => attachment.type === 'video'
      )

      return videoAttachment?.file?.attachUrl || null
    },

    coverImageUrl() {
      if (!this.questionData?.content?.attach) return null

      const coverAttachment = this.questionData.content.attach.find(
          attachment => attachment.type === 'cover'
      )

      return coverAttachment?.file?.attachUrl || null
    }
  },

  mounted() {
    this.initializeVideoPlayer()
  },

  beforeDestroy() {
    this.destroyHls()
  },

  watch: {
    videoUrl() {
      this.initializeVideoPlayer()
    }
  },

  methods: {
    initializeVideoPlayer() {
      if (!this.videoUrl) return

      this.destroyHls()

      const video = this.$refs.videoPlayer
      if (!video) return

      if (Hls.isSupported()) {
        this.hls = new Hls({
          enableWorker: false,
          lowLatencyMode: true,
          backBufferLength: 90
        })

        this.hls.loadSource(this.videoUrl)
        this.hls.attachMedia(video)

        this.hls.on(Hls.Events.MANIFEST_PARSED, () => {
          console.log('M3U8 manifest loaded successfully')
        })

        this.hls.on(Hls.Events.ERROR, (event, data) => {
          console.error('HLS error:', data)
          if (data.fatal) {
            switch (data.type) {
              case Hls.ErrorTypes.NETWORK_ERROR:
                console.error('Network error - trying to recover')
                this.hls.startLoad()
                break
              case Hls.ErrorTypes.MEDIA_ERROR:
                console.error('Media error - trying to recover')
                this.hls.recoverMediaError()
                break
              default:
                console.error('Fatal error - destroying HLS instance')
                this.destroyHls()
                break
            }
          }
        })
      } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
        // Safari native HLS support
        video.src = this.videoUrl
      } else {
        console.error('HLS is not supported in this browser')
      }
    },

    destroyHls() {
      if (this.hls) {
        this.hls.destroy()
        this.hls = null
      }
    }
  }
}
</script>

<style scoped>
.body-wrapper {
  display: flex;
  flex-direction: column;
  gap: 3px;
  width: 100%;
  height: 100%;
}

.video-wrapper {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  aspect-ratio: 16/9;
  background-color: #f5f5f5;
  position: relative;
  overflow: hidden;
  z-index: 1;
}

.video-container {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.video-element {
  width: 100%;
  height: 100%;
  object-fit: contain;
  background-color: #000;
}

.no-video-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  color: #667085;
}

.no-video-text {
  font-family: 'euclid_regular', sans-serif;
  font-size: 14px;
  color: #667085;
}

.tabs-view {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding: 10px;
  gap: 12px;
}

.active-tab {
  background-color: #040C2D;
  border-radius: 24px;
  padding: 0 16px;
  height: 26px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.active-tab-text {
  font-family: euclid_medium, sans-serif;
  font-size: 12px;
  line-height: 12px;
  color: #ffffff;
  text-align: center;
}

.divider {
  height: 6px;
  width: 100%;
  background-color: #F5F5F5;
}

.notes-wrapper {
  display: flex;
  flex-direction: column;
  flex: 1;
  align-items: center;
  justify-content: center;
}

.video-footer {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding: 10px;
  gap: 12px;
  height: 48px;
  border-top: 1px solid #EAECF0;
  z-index: 1;
}

.video-footer img {
  width: 24px;
  height: 24px;
}

.video-footer .text-input {
  flex: 1;
  height: 34px;
  border-radius: 9px;
  background-color: #F5F5F5;
  padding: 12px;
  font-family: euclid_regular, sans-serif;
  font-size: 12px;
  line-height: 12px;
  outline: none;
  border: none;
}

.note-holder {
  height: 50%;
  padding: 5px 2px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.note-title {
  font-family: 'euclid_semi_bold', sans-serif;
  font-size: 12px;
  color: #040C2D;
  text-align: center;
}

.note-info {
  font-family: 'euclid_regular', sans-serif;
  font-size: 10px;
  color: #667085;
  text-align: center;
}

.no-data-icon {
  width: 100px;
  height: 100px;
  margin-bottom: 10px;
}
</style>